// Single, refcounted patch over window.history.pushState/replaceState shared
// across every subscriber in the page. Hooks register a transformUrl/onAfter
// pair via subscribe(); the wrapper is installed on first subscribe and
// removed when the last subscriber leaves.
//
// Native invocation always goes through History.prototype.{pushState,replaceState}
// — never through a re-read of window.history.pushState — so that prior wrappers
// installed by other code (or by a stale module instance after HMR) cannot be
// captured-as-native and create a self-recursive call.
//
// Webapp and shell each ship their own copy of this module but resolve to the
// same window-level state object, so the order multiple MFEs come up in does
// not matter and React StrictMode's double-mount is harmless.

export interface HistorySubscriber {
  /** Optional URL transform applied before forwarding to the native call. */
  transformUrl?: (url: string | URL | null | undefined) => string | URL | null | undefined;
  /** Optional callback fired after each successful pushState/replaceState. */
  onAfter?: () => void;
}

interface HistoryPatchState {
  subscribers: Set<HistorySubscriber>;
}

const STATE_KEY = '__cuamHistoryPatchState__';

function getState(): HistoryPatchState {
  const w = window as unknown as Record<string, HistoryPatchState | undefined>;
  let state = w[STATE_KEY];
  if (!state) {
    state = { subscribers: new Set() };
    w[STATE_KEY] = state;
  }
  return state;
}

function install(state: HistoryPatchState) {
  window.history.pushState = function patchedPush(data, unused, url) {
    const finalUrl = applyTransforms(state, url);
    History.prototype.pushState.call(this, data, unused, finalUrl);
    notifyAfter(state);
  };
  window.history.replaceState = function patchedReplace(data, unused, url) {
    const finalUrl = applyTransforms(state, url);
    History.prototype.replaceState.call(this, data, unused, finalUrl);
    notifyAfter(state);
  };
}

function uninstall() {
  // Removing the own properties exposes History.prototype's natives again.
  const history = window.history as Partial<History>;
  delete history.pushState;
  delete history.replaceState;
}

function applyTransforms(state: HistoryPatchState, url: string | URL | null | undefined) {
  let result: string | URL | null | undefined = url;
  for (const sub of state.subscribers) {
    if (sub.transformUrl) result = sub.transformUrl(result);
  }
  return result;
}

function notifyAfter(state: HistoryPatchState) {
  for (const sub of state.subscribers) sub.onAfter?.();
}

/**
 * Register a history subscriber. Returns an unsubscribe function. Safe to call
 * from React effects, including under StrictMode double-mount: the refcount
 * tolerates churn and the wrapper is removed only when the last subscriber
 * unsubscribes.
 */
export function subscribe(sub: HistorySubscriber): () => void {
  const state = getState();
  if (state.subscribers.size === 0) install(state);
  state.subscribers.add(sub);
  return () => {
    state.subscribers.delete(sub);
    if (state.subscribers.size === 0) uninstall();
  };
}
